/**
 * Achievements for claimed (ranked) names — shared between the Leaderboard
 * Durable Object (which computes and stores unlocks) and the client menu
 * (which renders badges), so the registry and rules live in exactly one
 * place. Everything here is pure: no DO, no DOM, no sim dependency.
 *
 * Trust model mirrors ranked stats: achievement FACTS are observed by the
 * server-authoritative GameRoom sim replica during a multiplayer match and
 * posted to the Leaderboard with the internal match report — a hostile
 * client can no more forge an achievement than it can forge a rating. Bots
 * and unclaimed-name guests (playerId === null) never earn anything.
 */

export interface AchievementDef {
  name: string;
  desc: string;
  emoji: string;
}

/** Display order here is render order on the leaderboard. */
export const ACHIEVEMENTS: Record<string, AchievementDef> = {
  seed_round: {
    emoji: '🌱',
    name: 'Seed Round',
    desc: 'Win a ranked match.',
  },
  blitzscale: {
    emoji: '⚡',
    name: 'Blitzscale',
    desc: 'Win a ranked match in under 5 minutes.',
  },
  hostile_takeover: {
    emoji: '💸',
    name: 'Hostile Takeover',
    desc: 'Win a match in which a rival went bankrupt.',
  },
  down_round_survivor: {
    emoji: '🩹',
    name: 'Down Round Survivor',
    desc: 'Win a match after being insolvent during it.',
  },
  frontier_lab: {
    emoji: '🚀',
    name: 'Frontier Lab',
    desc: 'Ship a model generation that moves the global frontier.',
  },
  fast_follower: {
    emoji: '🧪',
    name: 'Fast Follower',
    desc: 'Catch up to the frontier with a fast-follow training run.',
  },
  compounding_returns: {
    emoji: '💠',
    name: 'Compounding Returns',
    desc: 'Max out fine-tuning jobs in a single match.',
  },
  margin_machine: {
    emoji: '🖨️',
    name: 'Margin Machine',
    desc: 'Hit +$100/s net cash flow in a ranked match.',
  },
  going_concern: {
    emoji: '📈',
    name: 'Going Concern',
    desc: 'Play 10 ranked matches.',
  },
} as const;

export type AchievementId = keyof typeof ACHIEVEMENTS;

/** Peak net cash flow ($/s) required for margin_machine. */
export const MARGIN_MACHINE_NET = 100;
/** Win faster than this (seconds) for blitzscale. */
export const BLITZSCALE_SEC = 300;
/** Ranked matches played for going_concern. */
export const GOING_CONCERN_GAMES = 10;

/**
 * Per-participant facts observed by the GameRoom's authoritative sim replica
 * over the course of one match. All optional so older rooms (or replays of
 * old payloads) degrade to "no new achievements" rather than erroring.
 */
export interface MatchFacts {
  won: boolean;
  durationSec: number;
  peakNet?: number;
  /** was insolvent (budget <= 0) at any point during the match */
  wasInsolvent?: boolean;
  /** shipped a generation that MOVED the global frontier */
  pushedFrontier?: boolean;
  /** shipped a generation via fast-follow (frontier already there) */
  fastFollowed?: boolean;
  /** reached the fine-tune job cap at some point (shipping resets the count,
   * so this is a high-water flag, not an end-state check) */
  maxedFineTunes?: boolean;
  /** some player in the match was eliminated by bankruptcy */
  rivalWentBankrupt?: boolean;
}

/**
 * Which achievements this match earns for one participant, given their
 * career stats AFTER the match's stats update. Pure; returns ids only —
 * union/dedup against already-unlocked achievements is the caller's job.
 */
export function computeAchievements(
  facts: MatchFacts,
  statsAfter: { games: number; wins: number },
): string[] {
  const earned: string[] = [];
  if (facts.won) earned.push('seed_round');
  if (facts.won && facts.durationSec < BLITZSCALE_SEC) earned.push('blitzscale');
  if (facts.won && facts.rivalWentBankrupt) earned.push('hostile_takeover');
  if (facts.won && facts.wasInsolvent) earned.push('down_round_survivor');
  if (facts.pushedFrontier) earned.push('frontier_lab');
  if (facts.fastFollowed) earned.push('fast_follower');
  if (facts.maxedFineTunes) earned.push('compounding_returns');
  if ((facts.peakNet ?? 0) >= MARGIN_MACHINE_NET) earned.push('margin_machine');
  if (statsAfter.games >= GOING_CONCERN_GAMES) earned.push('going_concern');
  return earned;
}

/**
 * Unions newly-earned ids into a stored unlock map (id -> unlockedAt epoch
 * ms). Already-unlocked ids keep their original timestamp; unknown ids are
 * dropped (a future server version can't corrupt an older row's display).
 * Returns a NEW object; never mutates.
 */
export function unionAchievements(
  existing: Record<string, number>,
  earned: string[],
  now: number,
): Record<string, number> {
  const next = { ...existing };
  for (const id of earned) {
    if (!(id in ACHIEVEMENTS)) continue;
    if (!(id in next)) next[id] = now;
  }
  return next;
}

/** Ids in canonical (registry) display order. */
export function sortAchievementIds(ids: string[]): string[] {
  const order = Object.keys(ACHIEVEMENTS);
  return [...ids].filter((id) => order.includes(id)).sort((a, b) => order.indexOf(a) - order.indexOf(b));
}
